import { __decorate, __param } from '../../../../../../../external/tslib/tslib.es6.js';
import { Emitter } from 'monaco-editor/esm/vs/base/common/event.js';
import { Disposable } from 'monaco-editor/esm/vs/base/common/lifecycle.js';
import { IInstantiationService } from 'monaco-editor/esm/vs/platform/instantiation/common/instantiation.js';
import { ILogService } from 'monaco-editor/esm/vs/platform/log/common/log.js';
import { ChatModelInitState } from './chatModel.js';
import { countWords } from './chatWordCounter.js';
function isRequestVM(item) {
    return !!item && typeof item === 'object' && 'message' in item;
}
function isResponseVM(item) {
    return !!item && typeof item.setVote !== 'undefined';
}
function isWelcomeVM(item) {
    return !!item && typeof item === 'object' && 'content' in item;
}
let ChatViewModel = class ChatViewModel extends Disposable {
    get inputPlaceholder() {
        return this._inputPlaceholder ?? this._model.inputPlaceholder;
    }
    setInputPlaceholder(text) {
        this._inputPlaceholder = text;
        this._onDidChange.fire({ kind: 'changePlaceholder' });
    }
    resetInputPlaceholder() {
        this._inputPlaceholder = undefined;
        this._onDidChange.fire({ kind: 'changePlaceholder' });
    }
    get sessionId() {
        return this._model.sessionId;
    }
    get requestInProgress() {
        return this._model.requestInProgress;
    }
    get providerId() {
        return this._model.providerId;
    }
    get initState() {
        return this._model.initState;
    }
    constructor(_model, instantiationService) {
        super();
        this._model = _model;
        this.instantiationService = instantiationService;
        this._onDidDisposeModel = this._register(( new Emitter()));
        this.onDidDisposeModel = this._onDidDisposeModel.event;
        this._onDidChange = this._register(( new Emitter()));
        this.onDidChange = this._onDidChange.event;
        this._items = [];
        this._inputPlaceholder = undefined;
        _model.getRequests().forEach((request, i) => {
            this._items.push(( new ChatRequestViewModel(request)));
            if (request.response) {
                this.onAddResponse(request.response);
            }
        });
        this._register(_model.onDidDispose(() => this._onDidDisposeModel.fire()));
        this._register(_model.onDidChange(e => {
            if (e.kind === 'addRequest') {
                this._items.push(( new ChatRequestViewModel(e.request)));
                if (e.request.response) {
                    this.onAddResponse(e.request.response);
                }
            }
            else if (e.kind === 'addResponse') {
                this.onAddResponse(e.response);
            }
            else if (e.kind === 'removeRequest') {
                const requestIdx = this._items.findIndex(item => isRequestVM(item) && item.id === e.requestId);
                if (requestIdx >= 0) {
                    this._items.splice(requestIdx, 1);
                }
                const responseIdx = e.responseId && this._items.findIndex(item => isResponseVM(item) && item.id === e.responseId);
                if (typeof responseIdx === 'number' && responseIdx >= 0) {
                    const items = this._items.splice(responseIdx, 1);
                    const item = items[0];
                    if (isResponseVM(item)) {
                        item.dispose();
                    }
                }
            }
            const modelEventToVmEvent = e.kind === 'addRequest' ? { kind: 'addRequest' } :
                e.kind === 'initialize' ? { kind: 'initialize' } :
                    null;
            this._onDidChange.fire(modelEventToVmEvent);
        }));
    }
    onAddResponse(responseModel) {
        const response = this.instantiationService.createInstance(ChatResponseViewModel, responseModel);
        this._register(response.onDidChange(() => this._onDidChange.fire(null)));
        this._items.push(response);
    }
    getItems() {
        return [...(this._model.welcomeMessage ? [this._model.welcomeMessage] : []), ...this._items];
    }
    dispose() {
        super.dispose();
        this._items
            .filter((item) => item instanceof ChatResponseViewModel)
            .forEach((item) => item.dispose());
    }
};
ChatViewModel = ( __decorate([
    ( __param(1, IInstantiationService))
], ChatViewModel));
class ChatRequestViewModel {
    get id() {
        return this._model.id;
    }
    get dataId() {
        return this.id + `_${ChatModelInitState[this._model.session.initState]}`;
    }
    get sessionId() {
        return this._model.session.sessionId;
    }
    get username() {
        return this._model.username;
    }
    get avatarIconUri() {
        return this._model.avatarIconUri;
    }
    get message() {
        return this._model.message;
    }
    get messageText() {
        return 'kind' in this.message ? this.message.message : this.message.text;
    }
    constructor(_model) {
        this._model = _model;
    }
}
let ChatResponseViewModel = class ChatResponseViewModel extends Disposable {
    get id() {
        return this._model.id;
    }
    get dataId() {
        return this._model.id + `_${this._modelChangeCount}` + `_${ChatModelInitState[this._model.session.initState]}`;
    }
    get providerId() {
        return this._model.providerId;
    }
    get sessionId() {
        return this._model.session.sessionId;
    }
    get username() {
        return this._model.username;
    }
    get avatarIconUri() {
        return this._model.avatarIconUri;
    }
    get agent() {
        return this._model.agent;
    }
    get slashCommand() {
        return this._model.slashCommand;
    }
    get response() {
        return this._model.response;
    }
    get usedContext() {
        return this._model.usedContext;
    }
    get contentReferences() {
        return this._model.contentReferences;
    }
    get progressMessages() {
        return this._model.progressMessages;
    }
    get isComplete() {
        return this._model.isComplete;
    }
    get isCanceled() {
        return this._model.isCanceled;
    }
    get replyFollowups() {
        return this._model.followups?.filter((f) => f.kind === 'reply');
    }
    get commandFollowups() {
        return this._model.followups?.filter((f) => f.kind === 'command');
    }
    get errorDetails() {
        return this._model.errorDetails;
    }
    get vote() {
        return this._model.vote;
    }
    get requestId() {
        return this._model.requestId;
    }
    get usedReferencesExpanded() {
        if (typeof this._usedReferencesExpanded === 'boolean') {
            return this._usedReferencesExpanded;
        }
        return this.response.value.length === 0;
    }
    set usedReferencesExpanded(v) {
        this._usedReferencesExpanded = v;
    }
    get vulnerabilitiesListExpanded() {
        return this._vulnerabilitiesListExpanded;
    }
    set vulnerabilitiesListExpanded(v) {
        this._vulnerabilitiesListExpanded = v;
    }
    get contentUpdateTimings() {
        return this._contentUpdateTimings;
    }
    constructor(_model, logService) {
        super();
        this._model = _model;
        this.logService = logService;
        this._modelChangeCount = 0;
        this._onDidChange = this._register(( new Emitter()));
        this.onDidChange = this._onDidChange.event;
        this.renderData = undefined;
        this._vulnerabilitiesListExpanded = false;
        this._contentUpdateTimings = undefined;
        if (!_model.isComplete) {
            this._contentUpdateTimings = {
                loadingStartTime: Date.now(),
                lastUpdateTime: Date.now(),
                impliedWordLoadRate: 0,
                lastWordCount: 0
            };
        }
        this._register(_model.onDidChange(() => {
            if (this._contentUpdateTimings) {
                const now = Date.now();
                const wordCount = countWords(_model.response.asString());
                const timeDiff = now - this._contentUpdateTimings.loadingStartTime;
                const impliedWordLoadRate = this._contentUpdateTimings.lastWordCount / (timeDiff / 1000);
                this.trace('onDidChange', `Update- got ${this._contentUpdateTimings.lastWordCount} words over ${timeDiff}ms = ${impliedWordLoadRate} words/s. ${wordCount} words are now available.`);
                this._contentUpdateTimings = {
                    loadingStartTime: this._contentUpdateTimings.loadingStartTime,
                    lastUpdateTime: now,
                    impliedWordLoadRate,
                    lastWordCount: wordCount
                };
            }
            else {
                this.logService.warn('ChatResponseViewModel#onDidChange: got model update but contentUpdateTimings is not initialized');
            }
            this._modelChangeCount++;
            this._onDidChange.fire();
        }));
    }
    trace(tag, message) {
        this.logService.trace(`ChatResponseViewModel#${tag}: ${message}`);
    }
    setVote(vote) {
        this._modelChangeCount++;
        this._model.setVote(vote);
    }
};
ChatResponseViewModel = ( __decorate([
    ( __param(1, ILogService))
], ChatResponseViewModel));
export { ChatRequestViewModel, ChatResponseViewModel, ChatViewModel, isRequestVM, isResponseVM, isWelcomeVM };
